/**
 * RegexPatternBuilder Component
 *
 * A visual regex builder that helps users construct AND-based patterns
 * by selecting text from an example transaction description.
 *
 * Features:
 * - Parse existing `(?=.*term)` patterns into visual chips
 * - Build patterns from terms array back to regex
 * - Highlight matching terms in example text
 * - Support text selection for adding new terms
 * - Editable example text mode for testing patterns
 *
 * @see docs/mockups/regex-builder-final-mockup.html - Design reference
 */

import { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import clsx from 'clsx';
import { X, Plus, Eraser } from 'lucide-react';

// =============================================================================
// Types
// =============================================================================

export interface RegexPatternBuilderProps {
  /** Current regex pattern */
  pattern: string;
  /** Callback when pattern changes */
  onPatternChange: (pattern: string) => void;
  /** Optional example text to display and highlight matches */
  exampleText?: string;
  /** Whether the example text is editable (contenteditable) */
  editableExample?: boolean;
  /** Callback when example text changes (only when editableExample is true) */
  onExampleTextChange?: (text: string) => void;
}

interface SelectionState {
  text: string;
  startOffset: number;
  endOffset: number;
}

// =============================================================================
// Helper Functions
// =============================================================================

/**
 * Escape special regex characters in a string.
 * Used when converting user-selected text into a regex pattern.
 *
 * @example
 * escapeRegex("$100.00") // returns "\\$100\\.00"
 * escapeRegex("hello world") // returns "hello world"
 */
export function escapeRegex(text: string): string {
  return text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/**
 * Parse a pattern with `(?=.*term)` lookaheads into an array of terms.
 * Handles escaped special characters in terms.
 *
 * @example
 * parsePattern("(?=.*MAZZINI)(?=.*FONDI)") // returns ["MAZZINI", "FONDI"]
 * parsePattern("(?=.*hello)") // returns ["hello"]
 * parsePattern("MAZZINI") // returns [] (not in lookahead format)
 */
export function parsePattern(pattern: string): string[] {
  if (!pattern) return [];

  const terms: string[] = [];
  // Match (?=.*content) groups, capturing the content
  // This regex handles escaped characters within the term
  const lookaheadRegex = /\(\?=\.\*([^)]+)\)/g;
  let match;

  while ((match = lookaheadRegex.exec(pattern)) !== null) {
    const term = match[1];
    // Unescape common regex escape sequences for display
    const unescaped = term
      .replace(/\\(.)/g, '$1'); // Remove escape backslashes
    terms.push(unescaped);
  }

  return terms;
}

/**
 * Build a pattern from an array of terms using AND logic (lookahead assertions).
 *
 * @example
 * buildPattern(["MAZZINI", "FONDI"]) // returns "(?=.*MAZZINI)(?=.*FONDI)"
 * buildPattern(["hello"]) // returns "(?=.*hello)"
 * buildPattern([]) // returns ""
 */
export function buildPattern(terms: string[]): string {
  if (terms.length === 0) return '';

  return terms
    .map((term) => `(?=.*${escapeRegex(term)})`)
    .join('');
}

// =============================================================================
// Sub-components
// =============================================================================

interface MatchHighlight {
  start: number;
  end: number;
  term: string;
}

/**
 * Find all match positions for terms in the text.
 * Returns sorted, non-overlapping highlight ranges.
 */
function findMatchHighlights(text: string, terms: string[]): MatchHighlight[] {
  if (!text || terms.length === 0) return [];

  const highlights: MatchHighlight[] = [];

  terms.forEach((term) => {
    if (!term) return;

    // Create a case-insensitive regex to find all occurrences
    try {
      const escapedTerm = escapeRegex(term);
      const regex = new RegExp(escapedTerm, 'gi');
      let match;

      while ((match = regex.exec(text)) !== null) {
        highlights.push({
          start: match.index,
          end: match.index + match[0].length,
          term,
        });
      }
    } catch {
      // Skip invalid regex patterns
    }
  });

  // Sort by start position
  highlights.sort((a, b) => a.start - b.start);

  // Remove overlapping highlights (keep earlier ones)
  const nonOverlapping: MatchHighlight[] = [];
  let lastEnd = -1;

  for (const highlight of highlights) {
    if (highlight.start >= lastEnd) {
      nonOverlapping.push(highlight);
      lastEnd = highlight.end;
    }
  }

  return nonOverlapping;
}

interface ExampleTextDisplayProps {
  /** The text to display */
  text: string;
  /** Terms to highlight in the text */
  terms: string[];
  /** Whether the text is editable */
  editable: boolean;
  /** Callback when text changes (if editable) */
  onTextChange?: (text: string) => void;
  /** Callback when text is selected */
  onSelectionChange: (selection: SelectionState | null) => void;
  /** Ref to selection actions container (to prevent clearing selection when clicking actions) */
  selectionActionsRef?: React.RefObject<HTMLDivElement | null>;
}

/**
 * ExampleTextDisplay sub-component
 *
 * Displays example text with highlighted matches and supports text selection.
 * Uses a textarea for editable mode to avoid contentEditable issues.
 */
function ExampleTextDisplay({
  text,
  terms,
  editable,
  onTextChange,
  onSelectionChange,
  selectionActionsRef,
}: ExampleTextDisplayProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const highlights = useMemo(() => findMatchHighlights(text, terms), [text, terms]);

  // Handle text selection from the highlighted text display (non-editable mode)
  const handleMouseUp = useCallback(() => {
    const selection = window.getSelection();
    if (!selection || selection.isCollapsed) {
      onSelectionChange(null);
      return;
    }

    const selectedText = selection.toString().trim();
    if (!selectedText) {
      onSelectionChange(null);
      return;
    }

    // Get the selection range relative to the container
    const range = selection.getRangeAt(0);
    const container = containerRef.current;
    if (!container) {
      onSelectionChange(null);
      return;
    }

    // Check if selection is within our container
    if (!container.contains(range.commonAncestorContainer)) {
      onSelectionChange(null);
      return;
    }

    onSelectionChange({
      text: selectedText,
      startOffset: range.startOffset,
      endOffset: range.endOffset,
    });
  }, [onSelectionChange]);

  // Handle text selection from textarea (editable mode)
  const handleTextareaMouseUp = useCallback(() => {
    const textarea = textareaRef.current;
    if (!textarea) {
      onSelectionChange(null);
      return;
    }

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;

    if (start === end) {
      onSelectionChange(null);
      return;
    }

    const selectedText = text.slice(start, end).trim();
    if (!selectedText) {
      onSelectionChange(null);
      return;
    }

    onSelectionChange({
      text: selectedText,
      startOffset: start,
      endOffset: end,
    });
  }, [text, onSelectionChange]);

  // Clear selection when clicking outside the container and selection actions
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const container = containerRef.current;
      const textarea = textareaRef.current;
      const selectionActions = selectionActionsRef?.current;
      const target = event.target as Node;

      // Don't clear selection if clicking within the container, textarea, or selection actions
      const isInsideContainer = container && container.contains(target);
      const isInsideTextarea = textarea && textarea.contains(target);
      const isInsideSelectionActions = selectionActions && selectionActions.contains(target);

      if (!isInsideContainer && !isInsideTextarea && !isInsideSelectionActions) {
        onSelectionChange(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [onSelectionChange, selectionActionsRef]);

  // Handle textarea input changes
  const handleTextareaChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
    if (onTextChange) {
      onTextChange(e.target.value);
    }
  }, [onTextChange]);

  // Render text with highlights (for non-editable mode)
  const renderHighlightedText = () => {
    if (highlights.length === 0) {
      return text;
    }

    const parts: React.ReactNode[] = [];
    let lastIndex = 0;

    highlights.forEach((highlight, i) => {
      // Add text before this highlight
      if (highlight.start > lastIndex) {
        parts.push(text.slice(lastIndex, highlight.start));
      }

      // Add highlighted text
      parts.push(
        <span
          key={`highlight-${i}`}
          className="bg-green-200 rounded px-0.5"
          data-testid="match-highlight"
        >
          {text.slice(highlight.start, highlight.end)}
        </span>
      );

      lastIndex = highlight.end;
    });

    // Add remaining text
    if (lastIndex < text.length) {
      parts.push(text.slice(lastIndex));
    }

    return parts;
  };

  // Editable mode: use a textarea for proper text input
  if (editable) {
    return (
      <textarea
        ref={textareaRef}
        value={text}
        onChange={handleTextareaChange}
        onMouseUp={handleTextareaMouseUp}
        onKeyUp={handleTextareaMouseUp}
        placeholder="Enter or paste a transaction description to test..."
        className={clsx(
          'w-full px-3 py-2 rounded-md border font-mono text-sm leading-relaxed resize-none',
          'bg-gray-50 border-gray-200',
          'min-h-[2.5rem]',
          'focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500',
          'placeholder:text-gray-400'
        )}
        rows={2}
        data-testid="example-text-display"
      />
    );
  }

  // Non-editable mode: use a div with highlighted text for selection
  return (
    <div
      ref={containerRef}
      className={clsx(
        'px-3 py-2 rounded-md border font-mono text-sm leading-relaxed',
        'bg-gray-50 border-gray-200',
        'cursor-text select-text'
      )}
      onMouseUp={handleMouseUp}
      data-testid="example-text-display"
    >
      {renderHighlightedText()}
    </div>
  );
}

// =============================================================================
// Pattern Chip Component
// =============================================================================

interface PatternChipProps {
  term: string;
  index: number;
  onRemove: (index: number) => void;
}

function PatternChip({ term, index, onRemove }: PatternChipProps) {
  return (
    <div
      className={clsx(
        'inline-flex items-center gap-1.5',
        'bg-gray-100 border border-gray-200 rounded-full',
        'px-2.5 py-1 text-sm'
      )}
      data-testid="pattern-chip"
    >
      {index > 0 && (
        <span className="bg-blue-100 text-blue-700 text-[10px] font-bold px-1.5 py-0.5 rounded uppercase">
          AND
        </span>
      )}
      <code className="text-emerald-600 font-mono">{term}</code>
      <button
        type="button"
        onClick={() => onRemove(index)}
        className={clsx(
          'w-4 h-4 flex items-center justify-center rounded-full',
          'text-gray-400 hover:text-gray-600 hover:bg-gray-200',
          'focus:outline-none focus:ring-2 focus:ring-blue-500'
        )}
        aria-label={`Remove term: ${term}`}
        data-testid="remove-chip-button"
      >
        <X size={12} />
      </button>
    </div>
  );
}

// =============================================================================
// Selection Actions Component
// =============================================================================

interface SelectionActionsProps {
  selectedText: string;
  onAddTerm: () => void;
  onReplace: () => void;
  hasExistingTerms: boolean;
  containerRef?: React.RefObject<HTMLDivElement | null>;
}

function SelectionActions({
  selectedText,
  onAddTerm,
  onReplace,
  hasExistingTerms,
  containerRef,
}: SelectionActionsProps) {
  return (
    <div
      ref={containerRef}
      className={clsx(
        'flex items-center gap-2 mt-2 p-2.5',
        'bg-blue-50 border border-blue-200 rounded-md'
      )}
      data-testid="selection-actions"
    >
      <div className="flex-1 text-sm text-blue-800 flex items-center gap-1.5">
        <span>Selected:</span>
        <code className="bg-blue-100 px-1.5 py-0.5 rounded font-mono font-medium">
          {selectedText}
        </code>
      </div>
      <button
        type="button"
        onClick={onReplace}
        className={clsx(
          'inline-flex items-center gap-1 px-2.5 py-1.5 text-sm font-medium rounded-md',
          'bg-blue-600 text-white hover:bg-blue-700',
          'focus:outline-none focus:ring-2 focus:ring-blue-500'
        )}
        data-testid="replace-button"
      >
        <Plus size={14} />
        Replace
      </button>
      {hasExistingTerms && (
        <button
          type="button"
          onClick={onAddTerm}
          className={clsx(
            'inline-flex items-center gap-1 px-2.5 py-1.5 text-sm font-medium rounded-md',
            'bg-gray-200 text-gray-700 hover:bg-gray-300',
            'focus:outline-none focus:ring-2 focus:ring-gray-500'
          )}
          data-testid="add-term-button"
        >
          <Plus size={14} />
          AND
        </button>
      )}
    </div>
  );
}

// =============================================================================
// Main Component
// =============================================================================

/**
 * RegexPatternBuilder Component
 *
 * A visual regex builder for creating AND-based patterns from example text.
 * Users can select text to add terms, and the pattern is automatically built.
 *
 * @example
 * <RegexPatternBuilder
 *   pattern="(?=.*MAZZINI)(?=.*FONDI)"
 *   onPatternChange={(pattern) => setPattern(pattern)}
 *   exampleText="BONIFICO DA MAZZINI DANIELE - RIF. FONDI IMMOBILIARE"
 * />
 */
export function RegexPatternBuilder({
  pattern,
  onPatternChange,
  exampleText = '',
  editableExample = false,
  onExampleTextChange,
}: RegexPatternBuilderProps) {
  // Parse pattern into terms
  const terms = useMemo(() => parsePattern(pattern), [pattern]);

  // Selection state
  const [selection, setSelection] = useState<SelectionState | null>(null);

  // Ref for selection actions container (passed to ExampleTextDisplay to prevent clearing selection on button click)
  const selectionActionsRef = useRef<HTMLDivElement>(null);

  // Local example text state for editable mode
  const [localExampleText, setLocalExampleText] = useState(exampleText);

  // Update local example text when prop changes
  useEffect(() => {
    setLocalExampleText(exampleText);
  }, [exampleText]);

  // Handle example text change
  const handleExampleTextChange = useCallback((text: string) => {
    setLocalExampleText(text);
    onExampleTextChange?.(text);
  }, [onExampleTextChange]);

  // Add a new term to the pattern
  const handleAddTerm = useCallback(() => {
    if (!selection?.text) return;

    const newTerms = [...terms, selection.text];
    onPatternChange(buildPattern(newTerms));
    setSelection(null);
  }, [selection, terms, onPatternChange]);

  // Replace all terms with the selected text
  const handleReplace = useCallback(() => {
    if (!selection?.text) return;

    onPatternChange(buildPattern([selection.text]));
    setSelection(null);
  }, [selection, onPatternChange]);

  // Remove a term from the pattern
  const handleRemoveTerm = useCallback((index: number) => {
    const newTerms = terms.filter((_, i) => i !== index);
    onPatternChange(buildPattern(newTerms));
  }, [terms, onPatternChange]);

  // Clear all terms
  const handleClear = useCallback(() => {
    onPatternChange('');
    setSelection(null);
  }, [onPatternChange]);

  // Check if pattern matches example text
  const patternMatches = useMemo(() => {
    if (!pattern || !localExampleText) return false;
    try {
      const regex = new RegExp(pattern, 'i');
      return regex.test(localExampleText);
    } catch {
      return false;
    }
  }, [pattern, localExampleText]);

  return (
    <div
      className="bg-gray-50 border border-gray-200 rounded-lg overflow-hidden"
      data-testid="regex-pattern-builder"
    >
      {/* Header */}
      <div className="px-3 py-2 border-b border-gray-200 flex items-center justify-between">
        <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
          Pattern Builder
        </span>
        <button
          type="button"
          onClick={handleClear}
          disabled={terms.length === 0}
          className={clsx(
            'inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded',
            'text-gray-600 hover:bg-gray-200',
            'disabled:opacity-50 disabled:cursor-not-allowed',
            'focus:outline-none focus:ring-2 focus:ring-gray-500'
          )}
          data-testid="clear-button"
        >
          <Eraser size={12} />
          Clear
        </button>
      </div>

      {/* Body */}
      <div className="p-3 bg-white">
        {/* Example Text Display */}
        <ExampleTextDisplay
          text={localExampleText}
          terms={terms}
          editable={editableExample}
          onTextChange={handleExampleTextChange}
          onSelectionChange={setSelection}
          selectionActionsRef={selectionActionsRef}
        />

        {/* Hint */}
        <p className="mt-1.5 text-xs text-gray-500">
          {editableExample
            ? 'Paste a transaction description, select text, then click a button below'
            : 'Select text above, then click a button below'}
        </p>

        {/* Selection Actions */}
        {selection && (
          <SelectionActions
            selectedText={selection.text}
            onAddTerm={handleAddTerm}
            onReplace={handleReplace}
            hasExistingTerms={terms.length > 0}
            containerRef={selectionActionsRef}
          />
        )}

        {/* Pattern Chips */}
        <div className="mt-3 pt-3 border-t border-gray-200">
          <div className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide mb-2">
            Conditions (all must match)
          </div>
          <div className="flex flex-wrap gap-2" data-testid="pattern-chips">
            {terms.length === 0 ? (
              <span className="text-sm text-gray-400 italic">
                No conditions added
              </span>
            ) : (
              terms.map((term, index) => (
                <PatternChip
                  key={`${term}-${index}`}
                  term={term}
                  index={index}
                  onRemove={handleRemoveTerm}
                />
              ))
            )}
          </div>
        </div>

        {/* Generated Regex Display */}
        {pattern && (
          <div
            className="mt-3 px-3 py-2 bg-gray-900 rounded-md"
            data-testid="generated-regex"
          >
            <code className="text-sm font-mono">
              <span className="text-gray-500">/</span>
              <span className="text-green-400">{pattern}</span>
              <span className="text-gray-500">/</span>
              <span className="text-blue-400">i</span>
            </code>
          </div>
        )}

        {/* Match Indicator */}
        {pattern && localExampleText && (
          <div
            className={clsx(
              'mt-3 px-3 py-2 rounded-md text-sm flex items-center gap-1.5',
              patternMatches
                ? 'bg-green-50 border border-green-200 text-green-700'
                : 'bg-yellow-50 border border-yellow-200 text-yellow-700'
            )}
            data-testid="match-indicator"
          >
            {patternMatches ? (
              <>
                <svg
                  className="w-4 h-4"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  aria-hidden="true"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M5 13l4 4L19 7"
                  />
                </svg>
                Pattern matches the example text
              </>
            ) : (
              <>
                <svg
                  className="w-4 h-4"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  aria-hidden="true"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                  />
                </svg>
                Pattern does not match the example text
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default RegexPatternBuilder;
